/**
 * Spec de interacciones de video interactivo (specVersion 2).
 * El JSON se guarda en video.interactions y lo consume el player de klazia3.0.
 *
 * Tipos:
 * - quiz_mcq
 * - voice_open (sustentación oral)
 * - voice_pronunciation (speaking / pronunciación)
 */

export const SPEC_VERSION = 2;

export const EMPTY_INTERACTIONS = { specVersion: SPEC_VERSION, interactions: [] };

export const INTERACTION_TYPE_OPTIONS = [
  { value: 'quiz_mcq', label: 'Pregunta (selección múltiple)' },
  { value: 'voice_open', label: 'Voz — sustentación oral' },
  { value: 'voice_pronunciation', label: 'Voz — pronunciación' },
];

function baseFields(anchorMs, type) {
  return {
    id: `i-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    type,
    anchorMs,
    pause: true,
    required: true,
    skipIfAnswered: true,
    retry: { maxAttempts: 2, showSolutionAfter: true },
    onComplete: { action: 'continue' },
  };
}

export function createQuizInteraction(anchorMs) {
  return {
    ...baseFields(anchorMs, 'quiz_mcq'),
    question: '',
    options: [
      { id: 'a', text: '', correct: true },
      { id: 'b', text: '', correct: false },
    ],
    shuffle: false,
  };
}

export function createVoiceOpenInteraction(anchorMs) {
  return {
    ...baseFields(anchorMs, 'voice_open'),
    question: '',
    expectedConcepts: [
      { id: 'c1', label: '' },
      { id: 'c2', label: '' },
    ],
    language: 'es-CO',
    maxSeconds: 60,
    passThreshold: 60,
  };
}

export function createVoicePronunciationInteraction(anchorMs) {
  return {
    ...baseFields(anchorMs, 'voice_pronunciation'),
    referenceText: '',
    question: '', // espejo opcional de referenceText para UI genérica
    language: 'en-US', // único locale por ahora; no se edita en el admin
    voiceName: 'en-US-JennyNeural',
    maxSeconds: 30,
    passThreshold: 70,
    figurative: '',
    countsTowardScore: false,
  };
}

export function createInteraction(type, anchorMs) {
  if (type === 'voice_open') return createVoiceOpenInteraction(anchorMs);
  if (type === 'voice_pronunciation') return createVoicePronunciationInteraction(anchorMs);
  return createQuizInteraction(anchorMs);
}

/**
 * Valida la lista de interacciones antes de guardar.
 * Devuelve un string con el primer error encontrado, o null si todo está bien.
 */
export function validateInteractions(interactions) {
  for (let i = 0; i < interactions.length; i++) {
    const item = interactions[i];
    const label = `Interacción ${i + 1}`;

    if (typeof item.anchorMs !== 'number' || item.anchorMs < 0) {
      return `${label}: el tiempo del video no es válido`;
    }

    if (item.type === 'quiz_mcq') {
      if (!item.question || !item.question.trim()) return `${label}: la pregunta está vacía`;
      if (!item.options || item.options.length < 2) return `${label}: debe tener al menos 2 opciones`;
      if (item.options.some((o) => !o.text || !o.text.trim())) {
        return `${label}: hay opciones sin texto`;
      }
      if (!item.options.some((o) => o.correct)) return `${label}: debe marcar una opción correcta`;
    }

    if (item.type === 'voice_open') {
      if (!item.question || !item.question.trim()) {
        return `${label}: la pregunta de sustentación está vacía`;
      }
      const concepts = (item.expectedConcepts || []).filter((c) => c.label && c.label.trim());
      if (concepts.length < 1) {
        return `${label}: agrega al menos un concepto esperado`;
      }
    }

    if (item.type === 'voice_pronunciation') {
      const phrase = item.referenceText || item.question;
      if (!phrase || !String(phrase).trim()) {
        return `${label}: la frase a pronunciar está vacía`;
      }
    }
  }
  return null;
}

export function formatMs(ms) {
  const totalSeconds = Math.floor((ms || 0) / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/**
 * Detecta la duración (en segundos) de un video por su URL leyendo solo el metadata.
 * Devuelve null si no se puede leer (URL inválida, formato no soportado, etc).
 */
export function detectVideoDuration(url) {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => resolve(Math.round(video.duration) || null);
    video.onerror = () => resolve(null);
    video.src = url;
  });
}
